import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, makeStyles, Paper } from '@material-ui/core';
import { showToast } from '@klpkonsern/ui';

import { useGetProsjektProcessByProcessIdQuery } from '@klp/prosjekt/services/rtkApi';
import { Main } from '@klp/layout';
import {
    ActionLink,
    Block,
    Heading,
    Loader,
    IconName,
    Illustration,
    StickyBox,
} from '@klp/design';
import { useCurrentUser } from '@klp/auth';
import { useFormAuth } from '@klp/formgenerator/hooks';

import type { ProjectViewParams } from '../config';
import { ProsjektRoutes } from '../config';
import { ProsjektForm } from '../components/Form/ProsjektForm';
import type {
    GodkjenningProsessResponse,
    ProjectTypeStatus,
} from '../interfaces';
import { ProsjektActions } from '../components/ProsjektActions/ProsjektActions';
import { prosjektSelectors } from '../store/prosjektSlice';
import { ProjectStepper } from '../components/ProjectStepper';
import { TildelKommentarList } from '../components/TildelKommentar/TildelKommentarList';
import { getRoleHolderBrukerId, translateProsjektSkjemaType } from '../utils';
import {
    activityIsHidden,
    convertFormFieldstoFormValues,
} from '@klp/formgenerator/utils';
import { TildelAnsvarligModal } from '../components/TildelAnsvarlig/TildelAnsvarligModal';
import { TildelAnsvarligInfobox } from '../components/TildelAnsvarlig/TildelAnsvarligInfobox';
import { FormContextProvider } from '@klp/formgenerator/index';

const useStyles = makeStyles((theme) => ({
    notFoundContainer: {
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '60%',
    },
    prosjektFormContainer: {
        flexGrow: 1,
        maxHeight: 'none',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1),
        },
    },
}));

export const ProsjektView = () => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const { prosessInstansId } = useParams<ProjectViewParams>();
    const { state } = useLocation<{
        routeBackToSummaryPage: boolean;
        selectedProcess: GodkjenningProsessResponse;
    }>();
    const [activeStep, setActiveStep] = useState<number>();
    const { currentUser } = useCurrentUser();
    const { isActivityEditable } = useFormAuth();

    const { activeModalProsessInstansId } = useSelector(prosjektSelectors);

    const {
        data: process,
        isLoading: processIsLoading,
        isFetching: processIsFetching,
        isError: processIsError,
    } = useGetProsjektProcessByProcessIdQuery({
        prosessInstansId:
            typeof state?.selectedProcess?.prosessInstansId === 'string'
                ? state?.selectedProcess?.prosessInstansId
                : prosessInstansId,
        culture: i18n.language,
    });

    const navigateBackBasedOnRoute = state?.routeBackToSummaryPage ?? false;

    // Sluttbruker skal ikke ha tilgang til å starte en ny prosess / aktivitet fra denne siden
    const tillatNesteSteg = undefined;

    if (processIsLoading || processIsFetching) {
        return <Loader isLoading={processIsLoading || processIsFetching} />;
    }

    if (processIsError || !process?.aktiviteter) {
        showToast({ body: t('general.error.message'), type: 'error' });

        return (
            <Main>
                <Block>
                    <ActionLink
                        actionType="linkBack"
                        translation="project.backButton"
                        href={ProsjektRoutes.projectSchema()}
                    />
                </Block>
                <Block
                    className={classes.notFoundContainer}
                    paddingTop="massive"
                >
                    <Illustration
                        paddingBottom="compact"
                        icon={IconName.CIRCLE_OBS}
                        size="xxxlarge"
                    />
                    <Heading
                        klpHeadingLevel="TWO"
                        klpColor="dark"
                        translation="project.notFound"
                        data-cy="Prosjekt__NotFound__Title"
                    />
                </Block>
            </Main>
        );
    }

    const prosjektTypeStatus: ProjectTypeStatus = {
        typeNavn: process.typeNavn,
        prosjektNavn: process.prosjekt?.navn,
        prosjektStatus: t(`prosjekt.statuses.${process?.prosjektStatus}`),
        prosjektskjemaType: translateProsjektSkjemaType(
            process?.prosjektskjemaType.typeNavn,
        ),
    };

    const allFormValues = process.aktiviteter.reduce(
        (acc, aktivitet) => ({
            ...acc,
            ...convertFormFieldstoFormValues(aktivitet.formFields),
        }),
        {},
    );

    const visibleAktiviteter = process.aktiviteter.filter(
        (aktivitet) => !activityIsHidden(aktivitet, allFormValues),
    );

    const selectedActivityIsEditable =
        activeStep !== undefined &&
        isActivityEditable(visibleAktiviteter[activeStep]);

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="prosjektSchema"
                    label="breadcrumbs.project.schema"
                    href={ProsjektRoutes.projectSchema()}
                />
                <Main.Crumb id="prosjektnavn" label={process.prosjekt?.navn} />
            </Main.Breadcrumbs>
            {activeModalProsessInstansId && process.currentAktivitet && (
                <TildelAnsvarligModal
                    prosjektId={process.prosjektId}
                    prosessInstansId={process.prosessInstansId}
                    kandidatGruppe={process.currentAktivitet.kandidatGruppe}
                    kandidatGruppeEier={
                        process.currentAktivitet.kandidatGruppeEier
                    }
                    currentAnsvarlig={
                        getRoleHolderBrukerId(
                            process,
                            process.currentAktivitet.kandidatGruppeEier,
                        ) ?? null
                    }
                    activity={process.currentAktivitet}
                    region={process.region}
                />
            )}
            <Main.Navigation>
                {navigateBackBasedOnRoute && (
                    <ActionLink
                        actionType="linkBack"
                        translation="project.backButtonHistorikk"
                        href={ProsjektRoutes.projectSummary(
                            process.prosessInstansId,
                            process.prosjektId,
                        )}
                    />
                )}
                {!navigateBackBasedOnRoute && (
                    <ActionLink
                        actionType="linkBack"
                        translation="project.backButton"
                        href={ProsjektRoutes.projectSchema()}
                    />
                )}
            </Main.Navigation>

            <ProjectStepper
                aktiviteter={visibleAktiviteter}
                processStatus={process.registreringStatus}
                callback={setActiveStep}
                process={process}
            />

            <FormContextProvider initialRegion={process.region}>
                <Block
                    padding="compact"
                    className={classes.prosjektFormContainer}
                >
                    {activeStep !== undefined && (
                        <Grid container={true} spacing={2}>
                            <Grid item={true} xs={12} md={8} lg={9}>
                                <ProsjektForm
                                    prosessInstansId={process.prosessInstansId}
                                    prosjektId={process.prosjektId}
                                    activity={visibleAktiviteter[activeStep]}
                                    prosjektTypeStatus={prosjektTypeStatus}
                                />
                            </Grid>
                            <Grid item={true} xs={12} md={4} lg={3}>
                                <StickyBox>
                                    <Paper elevation={1}>
                                        <ProsjektActions
                                            prosjekt={process}
                                            isEDAdmin={currentUser?.isEDAdmin}
                                            selectedElement="detaljer"
                                            allowedSteps={tillatNesteSteg}
                                            navigateOnDelete={true}
                                        />
                                    </Paper>
                                    {visibleAktiviteter[activeStep] && (
                                        <Block marginTop="compact">
                                            <Paper elevation={1}>
                                                <TildelAnsvarligInfobox
                                                    isDisabled={
                                                        visibleAktiviteter[
                                                            activeStep
                                                        ].formFields.some((f) =>
                                                            f.id.includes(
                                                                'Godkjent',
                                                            ),
                                                        ) ||
                                                        !selectedActivityIsEditable
                                                    }
                                                    prosjektId={
                                                        process.prosjektId
                                                    }
                                                    prosessInstansId={
                                                        process.prosessInstansId
                                                    }
                                                    activity={
                                                        visibleAktiviteter[
                                                            activeStep
                                                        ]
                                                    }
                                                    redirectTo={ProsjektRoutes.projectSchema()}
                                                />
                                                <TildelKommentarList
                                                    defaultExpanded={
                                                        selectedActivityIsEditable
                                                    }
                                                    currentActivity={
                                                        visibleAktiviteter[
                                                            activeStep
                                                        ]
                                                    }
                                                />
                                            </Paper>
                                        </Block>
                                    )}
                                </StickyBox>
                            </Grid>
                        </Grid>
                    )}
                </Block>
            </FormContextProvider>
        </Main>
    );
};
